// Copyright (C) 2020-2022 Intel Corporation
// Copyright (C) 2023 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import './styles.scss';
import React, { useEffect } from 'react';
import Layout from 'antd/lib/layout';

import CanvasLayout from 'components/annotation-page/canvas/grid-layout/canvas-layout';
import ControlsSideBarContainer from 'containers/annotation-page/standard-workspace/controls-side-bar/controls-side-bar';
import CanvasContextMenuContainer from 'containers/annotation-page/canvas/canvas-context-menu';
import ObjectsListContainer from 'containers/annotation-page/standard-workspace/objects-side-bar/objects-list';
import ObjectSideBarComponent from 'components/annotation-page/standard-workspace/objects-side-bar/objects-side-bar';
import CanvasPointContextMenuComponent from 'components/annotation-page/canvas/views/canvas2d/canvas-point-context-menu';
import IssueAggregatorComponent from 'components/annotation-page/review/issues-aggregator';
import RemoveConfirmComponent from 'components/annotation-page/standard-workspace/remove-confirm';
import PropagateConfirmComponent from 'components/annotation-page/standard-workspace/propagate-confirm';
import AudioSelector from '../audioselector/audioselector';
import AudioPlaybackComponent from './audio-playback';
import { CombinedState } from 'reducers';
import { connect } from 'react-redux';
import QuestionAnnotation from './questions-sidebar';
import TranscriptPlayer from '../transcript/transcript-player';
import WaveformSlider from '../transcript/waveform-slider';
import TranscriptReviewWorkspace from '../transcript-review-workspace/transcript-review-workspace';

interface StateToProps {
    jobPhase: string;
    showTranscript: boolean;
}

function mapStateToProps(state: CombinedState): StateToProps {
    const {
        annotation: {
            job: { instance: jobInstance },
            canvas: { showTranscript },
        },
    } = state;

    return {
        jobPhase: jobInstance.phase,
        showTranscript,
    };
}

function StandardWorkspaceComponent(props: StateToProps): JSX.Element {
    const { jobPhase, showTranscript } = props;

    useEffect(() => {
        // resize event window
        // window.dispatchEvent(new Event('resize'));
        // canvasInstance();
    }, [showTranscript]);

    if (['phase4a', 'phase4b'].includes(jobPhase)) {
        return <TranscriptReviewWorkspace />;
    }

    return (
        <>
            <AudioPlaybackComponent />
            <Layout hasSider className='cvat-standard-workspace'>
                {['phase0', 'phase1a', 'phase1b', 'phase2a', 'phase2b', 'phase3a', 'phase3b'].includes(jobPhase) && (
                    <ControlsSideBarContainer />
                )}
                <Layout.Content>
                    <Layout>
                        {['phase0', 'phase1a', 'phase1b'].includes(jobPhase) && (
                            <Layout.Header
                                style={{ padding: '10px', height: jobPhase === 'phase0' ? '15vh' : '10vh' }}
                                className='waveform-header'
                            >
                                <WaveformSlider />
                            </Layout.Header>
                        )}

                        {['phase1a'].includes(jobPhase) && <AudioSelector />}
                        <CanvasLayout showTranscript={showTranscript} />
                        {['phase2a', 'phase2b', 'phase3a', 'phase3b'].includes(jobPhase) && showTranscript && (
                            <>
                                <Layout.Footer
                                    className={'cvat-transcript-subtitles'}
                                    style={{
                                        height: 200,
                                        backgroundColor: '#001529',
                                        overflow: 'scroll',
                                    }}
                                >
                                    <TranscriptPlayer />
                                </Layout.Footer>
                            </>
                        )}
                    </Layout>
                </Layout.Content>
                {['phase0', 'phase1a', 'phase1b'].includes(jobPhase) && (
                    <Layout.Sider
                        width={jobPhase === 'phase0' ? '30vw' : '20vw'}
                        style={{
                            fontSize: 18,
                            overflowY: 'scroll',
                            minWidth: '200px',
                        }}
                    >
                        <TranscriptPlayer />
                    </Layout.Sider>
                )}
                {['phase0', 'phase1a', 'phase1b'].includes(jobPhase) ? (
                    <ObjectSideBarComponent
                        objectsList={<ObjectsListContainer readonly={!['phase0', 'phase1a'].includes(jobPhase)} />}
                    />
                ) : (
                    <Layout.Sider width={jobPhase === 'phase2b' ? '20vw' : '50vw'}>
                        <QuestionAnnotation />
                    </Layout.Sider>
                )}
                <PropagateConfirmComponent />
                <CanvasContextMenuContainer readonly={!['phase0', 'phase1a'].includes(jobPhase)} />
                <CanvasPointContextMenuComponent />
                <IssueAggregatorComponent />
                <RemoveConfirmComponent />
            </Layout>
        </>
    );
}

export default connect(mapStateToProps)(StandardWorkspaceComponent);
